"use client";
import React, { useRef, useState } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { Element } from "react-scroll";
import moment from "moment";

/* ─────────────────────────────────────────
   VARIANTS
───────────────────────────────────────── */
const fadeUp = {
  hidden: { y: 48, opacity: 0 },
  show: (d = 0) => ({
    y: 0,
    opacity: 1,
    transition: { duration: 0.75, delay: d, ease: [0.22, 1, 0.36, 1] },
  }),
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.16, delayChildren: 0.05 } },
};

const cardVariant = {
  hidden: { y: 56, opacity: 0 },
  show: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1] },
  },
};

/* ─────────────────────────────────────────
   DURATION BADGE — calculates tenure
───────────────────────────────────────── */
function tenure(start, end, isPresent) {
  const s = moment(start);
  const e = isPresent ? moment() : moment(end);
  const months = e.diff(s, "months");
  if (months < 12) return `${months}mo`;
  const y = Math.floor(months / 12);
  const m = months % 12;
  return m ? `${y}y ${m}mo` : `${y}y`;
}

/* ─────────────────────────────────────────
   TECH PILL
───────────────────────────────────────── */
function TechPill({ label }) {
  return (
    <motion.span
      whileHover={{ scale: 1.08, y: -2 }}
      transition={{ duration: 0.2 }}
      className="inline-flex items-center px-3 py-1 rounded-full text-[10px] tracking-[0.14em] uppercase
        border border-white/[0.07] bg-white/[0.03] text-white/45
        hover:border-[#00f0ff]/35 hover:text-[#00f0ff]/80 hover:bg-[#00f0ff]/[0.05]
        transition-colors duration-300 cursor-default"
      style={{ fontFamily: "'DM Mono', monospace" }}
    >
      {label}
    </motion.span>
  );
}

/* ─────────────────────────────────────────
   EXPERIENCE CARD
───────────────────────────────────────── */
function ExperienceCard({ experience, index, isLast }) {
  const {
    position,
    company_name,
    startTime,
    short_description,
    type,
    endTime,
    isPresent,
    company_location,
    technologies,
  } = experience;

  const [expanded, setExpanded] = useState(false);
  const ref = useRef(null);

  const dur = tenure(startTime, endTime, isPresent);
  const startLabel = startTime ? moment(startTime).format("MMM YYYY") : "";
  const endLabel = isPresent
    ? "Present"
    : endTime
      ? moment(endTime).format("MMM YYYY")
      : "";

  return (
    <motion.div
      variants={cardVariant}
      ref={ref}
      className="relative flex gap-6 md:gap-10"
    >
      {/* ── Timeline spine ── */}
      <div className="hidden md:flex flex-col items-center flex-shrink-0 pt-1">
        {/* Node */}
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          viewport={{ once: true }}
          transition={{
            duration: 0.5,
            delay: index * 0.12 + 0.2,
            ease: [0.22, 1, 0.36, 1],
          }}
          className="relative z-10 w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
          style={{
            background: "linear-gradient(135deg, #0047ff, #00f0ff)",
            boxShadow: "0 0 18px rgba(0,240,255,0.3)",
          }}
        >
          {/* Inner dark dot */}
          <div
            className="w-3 h-3 rounded-full"
            style={{ background: "#05050b" }}
          />
          {/* Pulse */}
          <motion.div
            className="absolute inset-0 rounded-full"
            style={{ border: "1px solid rgba(0,240,255,0.45)" }}
            animate={{ scale: [1, 1.7, 1], opacity: [0.5, 0, 0.5] }}
            transition={{ duration: 2.8, repeat: Infinity, delay: index * 0.5 }}
          />
          {/* Current indicator */}
          {isPresent && (
            <motion.div
              className="absolute -top-1 -right-1 w-3 h-3 rounded-full"
              style={{ background: "#00f0ff" }}
              animate={{ scale: [1, 1.3, 1], opacity: [1, 0.5, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          )}
        </motion.div>

        {/* Vertical line */}
        {!isLast && (
          <motion.div
            initial={{ scaleY: 0 }}
            whileInView={{ scaleY: 1 }}
            viewport={{ once: true }}
            transition={{
              duration: 1,
              delay: index * 0.12 + 0.5,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="w-px flex-1 mt-2 origin-top"
            style={{
              background:
                "linear-gradient(to bottom, #0047ff60, #00f0ff20, transparent)",
              minHeight: "60px",
            }}
          />
        )}
      </div>

      {/* ── Card body ── */}
      <motion.div
        whileHover={{ y: -3 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        className="flex-1 mb-10 md:mb-14 relative overflow-hidden rounded-2xl
          border border-white/[0.06] bg-white/[0.025] backdrop-blur-sm
          hover:border-[#00f0ff]/20 hover:bg-[#00f0ff]/[0.025]
          transition-colors duration-400 group"
      >
        {/* Inner top-left glow */}
        <div
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-2xl"
          style={{
            background:
              "linear-gradient(135deg, rgba(0,71,255,0.07) 0%, transparent 55%)",
          }}
        />

        {/* Top bracket */}
        <svg
          className="absolute top-3 left-3 opacity-15 group-hover:opacity-40 transition-opacity duration-300"
          width="16"
          height="16"
          fill="none"
        >
          <path d="M0 16 L0 0 L16 0" stroke="#00f0ff" strokeWidth="1.2" />
        </svg>
        <svg
          className="absolute top-3 right-3 opacity-15 group-hover:opacity-40 transition-opacity duration-300"
          width="16"
          height="16"
          fill="none"
        >
          <path d="M16 16 L16 0 L0 0" stroke="#0047ff" strokeWidth="1.2" />
        </svg>

        <div className="p-6 md:p-8">
          {/* ── Top row: company + meta ── */}
          <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
            <div>
              {/* Company name */}
              <h3
                className="text-xl md:text-2xl font-black text-white leading-tight"
                style={{ fontFamily: "'Syne', sans-serif" }}
              >
                {company_name}
              </h3>
              {/* Location */}
              {company_location && (
                <p
                  className="text-[11px] text-white/30 tracking-[0.18em] mt-0.5 uppercase"
                  style={{ fontFamily: "'DM Mono', monospace" }}
                >
                  ◎ {company_location}
                </p>
              )}
            </div>

            {/* Right meta cluster */}
            <div className="flex flex-col items-end gap-2 flex-shrink-0">
              {/* Date range */}
              <div
                className="flex items-center gap-2 text-[10px] tracking-[0.16em] text-white/35 uppercase"
                style={{ fontFamily: "'DM Mono', monospace" }}
              >
                <span>{startLabel}</span>
                <span className="text-white/20">—</span>
                <span className={isPresent ? "text-[#00f0ff]/70" : ""}>
                  {endLabel}
                </span>
              </div>

              {/* Badges row */}
              <div className="flex items-center gap-2">
                {/* Tenure */}
                <span
                  className="px-2.5 py-1 rounded-full text-[9px] tracking-[0.18em] uppercase
                    border border-[#0047ff]/25 bg-[#0047ff]/[0.07] text-[#00f0ff]/60"
                  style={{ fontFamily: "'DM Mono', monospace" }}
                >
                  {dur}
                </span>
                {/* Type */}
                {type && (
                  <span
                    className="px-2.5 py-1 rounded-full text-[9px] tracking-[0.18em] uppercase
                      border border-white/[0.08] bg-white/[0.03] text-white/35"
                    style={{ fontFamily: "'DM Mono', monospace" }}
                  >
                    {type}
                  </span>
                )}
                {/* Live badge */}
                {isPresent && (
                  <span
                    className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9px] tracking-[0.18em] uppercase
                      border border-[#00f0ff]/25 bg-[#00f0ff]/[0.05] text-[#00f0ff]/70"
                    style={{ fontFamily: "'DM Mono', monospace" }}
                  >
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00f0ff] opacity-60" />
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#00f0ff]" />
                    </span>
                    Current
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Divider */}
          <div
            className="h-px w-full mb-5"
            style={{
              background:
                "linear-gradient(90deg, #0047ff30, #00f0ff15, transparent)",
            }}
          />

          {/* ── Position ── */}
          <div className="flex items-center gap-3 mb-4">
            <span
              className="text-[10px] tracking-[0.22em] uppercase text-white/20"
              style={{ fontFamily: "'DM Mono', monospace" }}
            >
              Role
            </span>
            <div
              className="h-px flex-1 max-w-8"
              style={{ background: "#ffffff15" }}
            />
            <h4
              className="text-base md:text-lg font-bold"
              style={{
                fontFamily: "'Syne', sans-serif",
                background: "linear-gradient(90deg, #00f0ff, #0047ff)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              {position}
            </h4>
          </div>

          {/* ── Description ── */}
          {short_description && (
            <div className="relative mb-5">
              <AnimatePresence initial={false}>
                <motion.div
                  key={expanded ? "open" : "closed"}
                  initial={false}
                  animate={{ height: "auto" }}
                  className={!expanded ? "line-clamp-3" : ""}
                >
                  <p
                    className="text-sm md:text-base text-white/45 leading-relaxed"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  >
                    {short_description}
                  </p>
                </motion.div>
              </AnimatePresence>

              {short_description.length > 180 && (
                <button
                  onClick={() => setExpanded((v) => !v)}
                  className="mt-2 text-[10px] tracking-[0.18em] uppercase text-[#00f0ff]/50
                    hover:text-[#00f0ff] transition-colors duration-300 flex items-center gap-1.5"
                  style={{ fontFamily: "'DM Mono', monospace" }}
                >
                  {expanded ? "Show less ↑" : "Show more ↓"}
                </button>
              )}
            </div>
          )}

          {/* ── Tech stack ── */}
          {technologies?.length > 0 && (
            <div>
              <div className="flex items-center gap-3 mb-3">
                <span
                  className="text-[9px] tracking-[0.25em] uppercase text-white/18"
                  style={{ fontFamily: "'DM Mono', monospace" }}
                >
                  Stack
                </span>
                <div
                  className="h-px flex-1"
                  style={{
                    background:
                      "linear-gradient(90deg, #ffffff10, transparent)",
                  }}
                />
              </div>
              <div className="flex flex-wrap gap-2">
                {technologies.map((tech, i) => (
                  <TechPill key={i} label={tech} />
                ))}
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ─────────────────────────────────────────
   MAIN EXPERIENCE COMPONENT
───────────────────────────────────────── */
const Experience = ({ experienceData }) => {
  const headRef = useRef(null);
  const listRef = useRef(null);
  const headInView = useInView(headRef, { once: true, margin: "-60px" });
  const listInView = useInView(listRef, { once: true, margin: "-80px" });

  const data = (experienceData?.data ?? experienceData ?? []).sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
  );

  if (!data?.length) return null;

  return (
    <Element name="experience">
      <section
        className="relative overflow-hidden"
        style={{ background: "#05050b" }}
      >
        {/* Grid bg */}
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none opacity-[0.03]"
          aria-hidden
        >
          <defs>
            <pattern
              id="exp-grid"
              width="72"
              height="72"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 72 0 L 0 0 0 72"
                fill="none"
                stroke="white"
                strokeWidth="0.4"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#exp-grid)" />
        </svg>

        {/* Orbs */}
        <div
          className="absolute top-1/4 right-0 w-[450px] h-[450px] pointer-events-none"
          aria-hidden
        >
          <div
            style={{
              background:
                "radial-gradient(circle, rgba(0,71,255,0.1) 0%, transparent 70%)",
              filter: "blur(80px)",
              width: "100%",
              height: "100%",
            }}
          />
        </div>
        <div
          className="absolute bottom-1/4 left-0 w-[380px] h-[380px] pointer-events-none"
          aria-hidden
        >
          <div
            style={{
              background:
                "radial-gradient(circle, rgba(0,240,255,0.07) 0%, transparent 70%)",
              filter: "blur(80px)",
              width: "100%",
              height: "100%",
            }}
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-10 py-10 ">
          {/* ── HEADING ── */}
          <div ref={headRef} className="mb-20 lg:mb-24">
            <motion.div
              custom={0}
              variants={fadeUp}
              initial="hidden"
              animate={headInView ? "show" : "hidden"}
              className="flex items-center gap-4 mb-5"
            >
              <div className="h-px w-12 bg-gradient-to-r from-[#0047ff] to-[#00f0ff]" />
              <span
                className="text-[10px] tracking-[0.32em] text-[#00f0ff]/60 uppercase"
                style={{ fontFamily: "'DM Mono', monospace" }}
              >
                04 — Experience
              </span>
            </motion.div>

            <div className="overflow-hidden">
              <motion.h2
                custom={0.1}
                variants={fadeUp}
                initial="hidden"
                animate={headInView ? "show" : "hidden"}
                className="text-[clamp(3rem,9vw,7.5rem)] font-black leading-[0.88] tracking-tight text-white"
                style={{ fontFamily: "'Syne', sans-serif" }}
              >
                WORK
              </motion.h2>
            </div>
            <div className="overflow-hidden">
              <motion.h2
                custom={0.2}
                variants={fadeUp}
                initial="hidden"
                animate={headInView ? "show" : "hidden"}
                className="text-[clamp(3rem,9vw,7.5rem)] font-black leading-[0.88] tracking-tight"
                style={{
                  fontFamily: "'Syne', sans-serif",
                  WebkitTextStroke: "1.5px rgba(0,240,255,0.32)",
                  color: "transparent",
                }}
              >
                HISTORY.
              </motion.h2>
            </div>

            <motion.div
              initial={{ scaleX: 0 }}
              animate={headInView ? { scaleX: 1 } : {}}
              transition={{
                duration: 1,
                delay: 0.35,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="mt-6 h-px w-40 origin-left"
              style={{
                background:
                  "linear-gradient(90deg, #0047ff, #00f0ff60, transparent)",
              }}
            />

            <motion.p
              custom={0.38}
              variants={fadeUp}
              initial="hidden"
              animate={headInView ? "show" : "hidden"}
              className="mt-5 text-sm text-white/30 max-w-sm leading-relaxed"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              Engineering, leading, and shipping — the places that built the
              engineer.
            </motion.p>
          </div>

          {/* ── TIMELINE LIST ── */}
          <div ref={listRef}>
            <motion.div
              variants={stagger}
              initial="hidden"
              animate={listInView ? "show" : "hidden"}
            >
              {data.map((exp, i) => (
                <ExperienceCard
                  key={exp?._id ?? i}
                  experience={exp}
                  index={i}
                  isLast={i === data.length - 1}
                />
              ))}
            </motion.div>
          </div>

          {/* ── Footer stamp ── */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={listInView ? { opacity: 1 } : {}}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="flex items-center gap-4 mt-4"
          >
            <div
              className="h-px flex-1 max-w-7xl"
              style={{
                background:
                  "linear-gradient(90deg, transparent, #0047ff30, transparent)",
              }}
            />
            <span
              className="text-[9px] tracking-[0.28em] text-white/18 uppercase"
              style={{ fontFamily: "'DM Mono', monospace" }}
            >
              {data.length} position{data.length !== 1 ? "s" : ""}
            </span>
            <div
              className="h-px flex-1 max-w-7xl"
              style={{
                background:
                  "linear-gradient(90deg, transparent, #00f0ff20, transparent)",
              }}
            />
          </motion.div>
        </div>
      </section>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800;900&family=DM+Mono:ital,wght@0,300;0,400;0,500&family=DM+Sans:wght@300;400;500&display=swap');
      `}</style>
    </Element>
  );
};

export default Experience;
